/**
 * Geldfluss-Radar — „Wohin fließt das große Geld?"
 *
 * User-Vision (Ronnie): Die Großen (BlackRock, Bridgewater …) haben keine
 * Glaskugel — sie lesen systematisch Geldflüsse quer über alle Töpfe (Gold,
 * Bitcoin, Sektoren, Regionen) und handeln, BEVOR die Story in den Nachrichten
 * ist. Ihre Positionen sind geheim, aber ihre Fußspuren sind öffentlich:
 * Sammelt großes Geld einen Topf ein, läuft er besser als der Gesamtmarkt
 * (relative Stärke) — oft Monate vor der Schlagzeile (Beispiel: Rüstungswerte
 * drehten Ende 2021 nach oben, vor der Invasion).
 *
 * Kernsignal ist die WENDE, nicht der fertige Trend:
 *  - „dreht auf":  26 Wochen hinten, letzte 13 Wochen vorn → Geld BEGINNT zu
 *                  fließen = Frühsignal (früh dran statt Zug hinterher).
 *  - „Zufluss":    läuft schon; sehr weit gelaufen = „heiß" (spät!).
 *  - „dreht ab":   Zufluss bröckelt → Frühwarnung.
 *  - „Abfluss":    Geld zieht ab.
 *
 * Quellen der Methode: John Murphy (Intermarket-Analyse), Stan Weinstein
 * (Stage-Analyse/RS), Sam Stovall (Sektor-Rotation). Reine Funktion, testbar.
 */

export type FlowGroup = 'rohstoff' | 'krypto' | 'sektor' | 'region' | 'signal';
export type FlowState =
  | 'dreht_auf'
  | 'zufluss'
  | 'seitwaerts'
  | 'dreht_ab'
  | 'abfluss'
  | 'unbekannt';

export interface FlowPotDef {
  symbol: string;
  label: string;
  group: FlowGroup;
}

/** Vollständiges Radar-Universum. SPY ist der Vergleichsanker (nicht gelistet). */
export const FLOW_UNIVERSE: FlowPotDef[] = [
  // Rohstoffe / sichere Häfen
  { symbol: 'GLD', label: 'Gold', group: 'rohstoff' },
  { symbol: 'SLV', label: 'Silber', group: 'rohstoff' },
  // Krypto
  { symbol: 'BTC/USD', label: 'Bitcoin', group: 'krypto' },
  // US-Sektoren + Themen
  { symbol: 'SMH', label: 'Halbleiter / KI', group: 'sektor' },
  { symbol: 'XLK', label: 'Technologie', group: 'sektor' },
  { symbol: 'ITA', label: 'Rüstung (USA)', group: 'sektor' },
  { symbol: 'XLE', label: 'Energie', group: 'sektor' },
  { symbol: 'XLF', label: 'Banken / Finanzen', group: 'sektor' },
  { symbol: 'XLV', label: 'Gesundheit', group: 'sektor' },
  { symbol: 'XLI', label: 'Industrie', group: 'sektor' },
  { symbol: 'XLP', label: 'Konsum (Basis)', group: 'sektor' },
  { symbol: 'XLY', label: 'Konsum (zyklisch)', group: 'sektor' },
  { symbol: 'XLU', label: 'Versorger', group: 'sektor' },
  // Regionen
  { symbol: 'FXI', label: 'China', group: 'region' },
  { symbol: 'INDA', label: 'Indien', group: 'region' },
  { symbol: 'EEM', label: 'Schwellenländer', group: 'region' },
  { symbol: 'ILF', label: 'Südamerika', group: 'region' },
  { symbol: 'AFK', label: 'Afrika', group: 'region' },
  { symbol: 'VGK', label: 'Europa', group: 'region' },
  // Signalgeber (keine Kauf-Töpfe — sie erklären das Umfeld)
  { symbol: 'TLT', label: 'US-Anleihen', group: 'signal' },
  { symbol: 'UUP', label: 'US-Dollar', group: 'signal' },
];

/** Symbole, die der Regime-Hook noch NICHT lädt (extra zu holen). */
export const EXTRA_FLOW_SYMBOLS = [
  'SLV',
  'SMH',
  'ITA',
  'FXI',
  'INDA',
  'EEM',
  'ILF',
  'AFK',
  'VGK',
  'UUP',
] as const;

/** Minimaler Quote-Ausschnitt — hält die Lib unabhängig vom Provider-Typ. */
export interface FlowQuoteLike {
  return_13w_pct: number | null;
  return_26w_pct: number | null;
}

export interface FlowPot {
  symbol: string;
  label: string;
  group: FlowGroup;
  state: FlowState;
  /** Vorsprung vs. SPY in Prozentpunkten, letzte 13 Wochen. */
  rs13: number | null;
  /** Vorsprung vs. SPY in Prozentpunkten, 26 Wochen. */
  rs26: number | null;
  /** Sehr weit gelaufen — Einstieg wäre spät („nicht hinterherlaufen"). */
  hot: boolean;
  /** Nur für Signalgeber: was ihr Fluss fürs Umfeld bedeutet. */
  signal_hint: string | null;
}

export interface FlowRadar {
  /** Alle Kauf-Töpfe (ohne Signalgeber), sortiert: Frühsignale zuerst. */
  pots: FlowPot[];
  /** Nur die Frühsignale („dreht auf"), stärkste zuerst. */
  frueh: FlowPot[];
  /** Signalgeber (Anleihen, Dollar) mit Klartext-Deutung. */
  signale: FlowPot[];
  /** false = zu wenig Daten (SPY fehlt oder fast alles leer). */
  known: boolean;
}

/** Wende-Schwelle in Prozentpunkten — filtert Rauschen. */
const TURN_PP = 1.5;
/** Ab diesem 26W-Vorsprung gilt ein Topf als „heiß" (spät). */
const HOT_PP = 12;

const STATE_ORDER: Record<FlowState, number> = {
  dreht_auf: 0,
  zufluss: 1,
  seitwaerts: 2,
  dreht_ab: 3,
  abfluss: 4,
  unbekannt: 5,
};

function classify(rs13: number | null, rs26: number | null): FlowState {
  if (rs13 === null || rs26 === null) return 'unbekannt';
  if (rs26 <= 0 && rs13 >= TURN_PP) return 'dreht_auf';
  if (rs26 > 0 && rs13 <= -TURN_PP) return 'dreht_ab';
  if (rs26 > 1) return 'zufluss';
  if (rs26 < -1) return 'abfluss';
  return 'seitwaerts';
}

function signalHint(symbol: string, state: FlowState): string | null {
  const rein = state === 'zufluss' || state === 'dreht_auf';
  const raus = state === 'abfluss' || state === 'dreht_ab';
  if (symbol === 'UUP') {
    if (rein) return 'Dollar stark → Gegenwind für Gold & Schwellenländer.';
    if (raus) return 'Dollar schwach → Rückenwind für Gold & Schwellenländer.';
    return 'Dollar unauffällig — kein Extra-Signal.';
  }
  if (symbol === 'TLT') {
    if (rein) return 'Anleihen gefragt → Anleger suchen Sicherheit (Risiko-Scheu).';
    if (raus) return 'Anleihen gemieden → Risikofreude oder Zinssorgen.';
    return 'Anleihen unauffällig — kein Extra-Signal.';
  }
  return null;
}

export function computeMoneyFlowRadar(
  quotes: Map<string, FlowQuoteLike | null | undefined>
): FlowRadar {
  const spy = quotes.get('SPY') ?? null;
  const spy13 = spy?.return_13w_pct ?? null;
  const spy26 = spy?.return_26w_pct ?? null;

  const all: FlowPot[] = FLOW_UNIVERSE.map((def) => {
    const q = quotes.get(def.symbol) ?? null;
    const r13 = q?.return_13w_pct ?? null;
    const r26 = q?.return_26w_pct ?? null;
    const rs13 = r13 !== null && spy13 !== null ? r13 - spy13 : null;
    const rs26 = r26 !== null && spy26 !== null ? r26 - spy26 : null;
    const state = classify(rs13, rs26);
    return {
      symbol: def.symbol,
      label: def.label,
      group: def.group,
      state,
      rs13,
      rs26,
      hot: state === 'zufluss' && rs26 !== null && rs26 >= HOT_PP,
      signal_hint: def.group === 'signal' ? signalHint(def.symbol, state) : null,
    };
  });

  const sortPots = (a: FlowPot, b: FlowPot) => {
    const so = STATE_ORDER[a.state] - STATE_ORDER[b.state];
    if (so !== 0) return so;
    return (b.rs13 ?? -Infinity) - (a.rs13 ?? -Infinity);
  };

  const pots = all.filter((p) => p.group !== 'signal').sort(sortPots);
  const signale = all.filter((p) => p.group === 'signal');
  const frueh = pots.filter((p) => p.state === 'dreht_auf');

  const withData = pots.filter((p) => p.state !== 'unbekannt').length;
  const known = spy13 !== null && spy26 !== null && withData >= 5;

  return { pots, frueh, signale, known };
}
